import { Button, PasswordField, Spinner, useTranslation } from '@hbs-us/ui';
import { type FormEvent, useEffect, useId, useMemo, useState } from 'react';

import { Input } from '../../components/ui/Input.js';
import { ApiError, apiFetchJson } from '../../lib/apiFetch.js';

const MIN_PASSWORD_LENGTH = 8;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface JoinLookupResponse {
  ok: true;
  tenantName: string;
}

type LookupState =
  | { status: 'loading' }
  | { status: 'ready'; tenantName: string }
  | { status: 'invalid' };

function submitErrorKey(error: unknown): string {
  if (error instanceof ApiError) {
    if (
      error.status === 404 ||
      error.message.includes('invalid') ||
      error.message.includes('expired')
    ) {
      return 'join:error.invalid';
    }
  }
  return 'join:error.submit';
}

export function JoinRoute({ code }: { code: string }) {
  const { t } = useTranslation();
  const nameId = useId();
  const emailId = useId();
  const [lookup, setLookup] = useState<LookupState>({ status: 'loading' });
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  // `pending`: the company requires approval, so the account exists but cannot sign in yet.
  const [completed, setCompleted] = useState<false | 'ready' | 'pending'>(false);

  useEffect(() => {
    let cancelled = false;
    setLookup({ status: 'loading' });
    setCompleted(false);
    setSubmitError(null);
    void apiFetchJson<JoinLookupResponse>(`/api/v1/join/${encodeURIComponent(code)}`)
      .then((result) => {
        if (cancelled) return;
        setLookup({ status: 'ready', tenantName: result.tenantName });
      })
      .catch(() => {
        if (cancelled) return;
        setLookup({ status: 'invalid' });
      });

    return () => {
      cancelled = true;
    };
  }, [code]);

  const validationError = useMemo(() => {
    if (name.trim() === '') return t('join:validation.nameRequired');
    if (!EMAIL_RE.test(email.trim())) return t('join:validation.emailInvalid');
    if (password.length < MIN_PASSWORD_LENGTH) {
      return t('join:validation.passwordMin', { count: MIN_PASSWORD_LENGTH });
    }
    if (confirmPassword !== password) return t('join:validation.passwordMismatch');
    return null;
  }, [confirmPassword, email, name, password, t]);

  const canSubmit =
    lookup.status === 'ready' && !completed && !submitting && validationError === null;

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const result = await apiFetchJson<{ ok: true; pendingApproval?: boolean }>(
        `/api/v1/join/${encodeURIComponent(code)}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name: name.trim(),
            email: email.trim().toLowerCase(),
            password,
          }),
        },
      );
      setCompleted(result.pendingApproval ? 'pending' : 'ready');
      setPassword('');
      setConfirmPassword('');
    } catch (error) {
      setSubmitError(submitErrorKey(error));
    } finally {
      setSubmitting(false);
    }
  };

  if (lookup.status === 'loading') {
    return (
      <main className="flex h-full items-center justify-center bg-background p-6">
        <Spinner label={t('common:status.loading')} />
      </main>
    );
  }

  if (lookup.status === 'invalid') {
    return (
      <main className="flex h-full items-center justify-center bg-background p-6">
        <section
          role="alert"
          aria-live="assertive"
          className="w-full max-w-md rounded-xl border border-border bg-card p-8 text-center shadow-lg"
        >
          <h1 className="font-semibold text-foreground text-xl">{t('join:invalid.heading')}</h1>
          <p className="mt-2 text-muted-foreground text-sm">{t('join:invalid.body')}</p>
          <a
            href="/"
            className="mt-6 inline-flex h-11 items-center justify-center rounded-md bg-secondary px-4 font-medium text-secondary-foreground text-sm transition-colors hover:bg-secondary/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
          >
            {t('join:loginCta')}
          </a>
        </section>
      </main>
    );
  }

  return (
    <main className="flex h-full items-center justify-center bg-background p-6">
      <section className="w-full max-w-md rounded-xl border border-border bg-card p-8 shadow-lg">
        {completed === 'pending' ? (
          <div>
            <h1 className="font-semibold text-foreground text-xl">{t('join:pending.heading')}</h1>
            <p className="mt-2 text-muted-foreground text-sm">
              {t('join:pending.body', {
                tenantName: lookup.tenantName,
              })}
            </p>
          </div>
        ) : completed ? (
          <div>
            <h1 className="font-semibold text-foreground text-xl">{t('join:success.heading')}</h1>
            <p className="mt-2 text-muted-foreground text-sm">{t('join:success.body')}</p>
            <a
              href="/"
              className="mt-6 inline-flex h-11 items-center justify-center rounded-md bg-primary px-4 font-medium text-primary-foreground text-sm transition-colors hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
            >
              {t('join:loginCta')}
            </a>
          </div>
        ) : (
          <>
            <h1 className="font-semibold text-foreground text-xl">
              {t('join:heading', { tenantName: lookup.tenantName })}
            </h1>
            <p className="mt-2 text-muted-foreground text-sm">{t('join:subheading')}</p>

            <form onSubmit={submit} className="mt-6 flex flex-col gap-4">
              <div className="flex flex-col gap-1.5">
                <label htmlFor={nameId} className="font-medium text-foreground text-sm">
                  {t('join:nameLabel')}
                </label>
                <Input
                  id={nameId}
                  value={name}
                  autoComplete="name"
                  onChange={(event) => setName(event.target.value)}
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <label htmlFor={emailId} className="font-medium text-foreground text-sm">
                  {t('join:emailLabel')}
                </label>
                <Input
                  id={emailId}
                  type="email"
                  value={email}
                  autoComplete="email"
                  onChange={(event) => setEmail(event.target.value)}
                />
              </div>

              {/*
                The rule under the box is the api worker's own: `validatePassword`
                (qelm-backend/workers/api/src/lib/validation.ts) rejects anything shorter
                than 8, and POST /api/v1/join/:code calls it. MIN_PASSWORD_LENGTH is the
                same constant this form already validates against, so the printed rule
                cannot drift from the one enforced here.
              */}
              <PasswordField
                className="bg-background"
                label={t('join:passwordLabel')}
                placeholder={t('common:password.newPlaceholder')}
                helperText={t('common:password.minLength', { count: MIN_PASSWORD_LENGTH })}
                showLabel={t('common:password.show')}
                hideLabel={t('common:password.hide')}
                value={password}
                autoComplete="new-password"
                onChange={(event) => setPassword(event.target.value)}
              />

              <PasswordField
                className="bg-background"
                label={t('join:confirmPasswordLabel')}
                placeholder={t('common:password.confirmPlaceholder')}
                helperText={t('common:password.mustMatch')}
                showLabel={t('common:password.show')}
                hideLabel={t('common:password.hide')}
                value={confirmPassword}
                autoComplete="new-password"
                onChange={(event) => setConfirmPassword(event.target.value)}
              />

              <p role="alert" aria-live="assertive" className="min-h-5 text-destructive text-sm">
                {submitError ? t(submitError) : (validationError ?? '')}
              </p>

              <Button type="submit" disabled={!canSubmit} className="mt-1 w-full">
                {submitting ? t('join:submitting') : t('join:submit')}
              </Button>
            </form>
          </>
        )}
      </section>
    </main>
  );
}
